import { MDBInput } from "mdb-react-ui-kit";
import { CSSProperties, useEffect, useRef, useState, Ref } from "react";
import { useTranslation } from "react-i18next";

const STANDARD_ZINDEX = 105;

export interface CustomSelectItemProps {
    val: number|string;
    title: string;
    icon?: string;
    iconSrc?: string;
    iconColor?: string;
}

export interface CustomSelectProps {
    items: CustomSelectItemProps[];
    selected: string|number|null;
    onSelect: (id: string|number) => void;

    width?: number;
    defaultTitle?: string;
    expandWidth?: number;
    title?: string;
    w100?: boolean;
    disabled?: boolean;
    search?: boolean;
    style?: CSSProperties;

    zIndex?: number;
    // When true, renders a check icon in the dropdown list for the selected item only (not in the input)
    selectedCheckInList?: boolean;
}

export const CustomSelect = ({width, selected, items, onSelect, defaultTitle, expandWidth, title, w100, disabled, search, style, zIndex, selectedCheckInList}: CustomSelectProps) => {
    const [selectedItem, setSelectedItem] = useState(selected);
    const [selItem, setSelItem] = useState<CustomSelectItemProps|null>(null);
    const [opened, setOpened] = useState(false);
    const [searchText, setSearchText] = useState('');
    const input = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const selectedItemRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedItem(selected);
    }, [selected]);

    useEffect(() => {
        setSearchText('');
    }, [opened]);

    useEffect(() => {
        setSelItem(items.find(it => it.val == selected)||null);
    }, [items, selected]);

    // When opening, scroll the dropdown list to the selected item
    useEffect(() => {
        if (!opened) return;
        const container = listRef.current;
        const itemEl = selectedItemRef.current;
        if (!container || !itemEl) return;
        // Use rAF to ensure layout is settled
        requestAnimationFrame(() => {
            const itemTop = itemEl.offsetTop;
            const itemHeight = itemEl.offsetHeight;
            const target = Math.max(0, itemTop - Math.max(0, (container.clientHeight - itemHeight) / 2));
            container.scrollTop = target;
        });
    }, [opened, selectedItem]);

    return (
        <div className={w100 ? "w-100" : ""} style={style}>
            <div
                ref={input}
                className="position-relative d-flex flex-row pt-1 pb-0"
                style={{alignItems: 'center', width: width || ''}}
                onClick={() => setOpened(!disabled && !opened)}
            >
                <CustomSelectSelectedItem
                    title={title}
                    defaultTitle={defaultTitle}
                    item={selItem}
                    disabled={disabled}
                    zIndex={(zIndex ?? STANDARD_ZINDEX) + 2}
                />
                {!disabled && <>
                    <span style={{display: !opened ? 'block' : 'none', position: 'absolute', right: 5}}><i className="fas trailing fa-angle-down fa-fw" ></i></span>
                    <span style={{display: opened ? 'block' : 'none', position: 'absolute', right: 5}}><i className="fas trailing fa-angle-up fa-fw"></i></span>
                </>}
            </div>

            <div
                style={{position: 'fixed', height: '100vh', left: 0, top: 0, width: '100vw', zIndex: zIndex ?? STANDARD_ZINDEX, display: opened ? 'block' : 'none'}}
                onClick={() => setOpened(false)}
            />

            {opened &&
                <div
                    className="card custom-select-box"
                    style={{
                        position: 'fixed', zIndex: (zIndex ?? STANDARD_ZINDEX) + 1, display: opened ? 'block' : 'none',
                        top: (input.current?.getBoundingClientRect().y ?? 0) + (input.current?.getBoundingClientRect().height ?? 0),
                        left: input.current?.getBoundingClientRect().x ?? 0, 
                        maxHeight: 
                        (input.current && input.current.getBoundingClientRect().bottom > window.innerHeight)
                            ? (input.current.getBoundingClientRect().height - (input.current.getBoundingClientRect().bottom - window.innerHeight))
                            : undefined,
                        width: (expandWidth ?? width ?? (input.current?.getBoundingClientRect().width ?? 0))
                    }}
                >
                    {search && <div>
                        <div className="input-group rounded p-1">
                            <MDBInput type="search" label={t('search')} aria-label="Search" aria-describedby="search-addon" value={searchText} onChange={ev => setSearchText(ev.currentTarget.value)}/>
                            <span className="input-group-text border-0" id="search-addon">
                                <i className="fas fa-search"></i>
                            </span>
                        </div>
                    </div>}
                    <div className="custom-select-body" ref={listRef} style={{maxHeight: search ? 200 : 250, overflowY: 'auto'}}>
                        {
                            items.map(item => (
                                <CustomSelectItem
                                    key={item.val}
                                    item={item}
                                    hidden={item.title.toLocaleUpperCase().indexOf(searchText.toLocaleUpperCase()) < 0}
                                    isSelected={item.val === selectedItem}
                                    selectedCheckInList={!!selectedCheckInList}
                                    selectedRef={item.val === selectedItem ? selectedItemRef : undefined}
                                    onSelect={() => {
                                        setSelectedItem(item.val);
                                        setOpened(false);
                                        if (onSelect) onSelect(item.val);
                                    }}
                                />
                            ))
                        }
                    </div>
                </div>
            
            }
            
        </div>
        
    )
}

const CustomSelectItem = (props: {
    item: CustomSelectItemProps,
    hidden: boolean,
    isSelected: boolean,
    selectedCheckInList: boolean,
    selectedRef?: Ref<HTMLDivElement>,
    onSelect: () => void
}) => (
    <div
        className="custom-select-item"
        ref={props.selectedRef}
        title={props.item.title}
        onClick={props.onSelect}
        style={{ display: props.hidden ? 'none' : undefined }}
    >
        {props.item.icon &&
            <i className={props.item.icon+' me-1'} style={{color: props.item.iconColor||''}}/>
        }
        {props.item.iconSrc &&
            <img src={props.item.iconSrc} className="me-1" alt=""/>
        }
        <span className="flex-grow-1" style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{props.item.title}</span>
        {props.selectedCheckInList && props.isSelected && (
            <i className="fas fa-check text-primary ms-2" />
        )}
    </div>
);

const CustomSelectSelectedItem = ({item, disabled, defaultTitle, title, zIndex}: {
    item: CustomSelectItemProps|null,
    disabled?: boolean,
    defaultTitle?: string,
    title?: string,
    zIndex: number
}) => {
    const { t } = useTranslation();

    return (
        <div className="w-100 h-100" style={{backgroundColor: disabled ? '#e9ecef' : ''}}>
            {item && item.icon &&
                <span style={{position: 'absolute', zIndex: zIndex, left: 5, top: 9}}>
                    <i className={item.icon+' me-1 fa-fw'} style={{color: item.iconColor||''}}/>
                </span>
            }
            <MDBInput
                type="text" className={"pe-3 " + (disabled ? '' : 'bg-white') + (item ? '' : ' fst-italic')}
                style={{overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'none', cursor: disabled ? '' : 'pointer', paddingLeft: item && item.icon ? 28 : ''}}
                readOnly id="userName" disabled={disabled}
                value={item ? item.title : (defaultTitle||t('choose_from_list'))}
                label={title||""}
            />
        </div>
    )
};
